import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, FormControlLabel, IconButton, InputAdornment, MenuItem,
  Pagination, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { MdAdd, MdAdminPanelSettings, MdClose, MdDeleteOutline, MdEdit, MdGroups, MdSearch, MdVisibility, MdWarehouse } from 'react-icons/md';
import { deleteData, getData, postData, putData } from '../../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';
const LIMIT = 10;

const authToken = () => {
  const raw = sessionStorage.getItem('token') || localStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const niceName = (n) => (n ? String(n).replace(/_/g, ' ') : '—');
const forLabel = (v) => (v === 'warehouse' ? 'Warehouse' : 'Admin CRM');
const forColor = (v) => (v === 'warehouse' ? { bg: '#ffedd5', fg: '#9a3412' } : { bg: '#e0e7ff', fg: '#3730a3' });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const idOf = (m) => String(m?._id || m);

const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } };
const iconBtn = (c) => ({ color: c, background: `${c}14`, borderRadius: '8px', '&:hover': { background: `${c}26` } });
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };

const Kpi = ({ title, value, icon: Icon, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 180, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, display: 'flex', alignItems: 'center', gap: 1.5, cursor: onClick ? 'pointer' : 'default', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '12px', p: 1.1, display: 'flex' }}><Icon size={22} /></Box>
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
    </Box>
  </Box>
);

const Card = ({ title, accent, children, right }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.25 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5 }}>{title}</Typography>
      {right}
    </Box>
    {children}
  </Box>
);

const emptyForm = { id: null, name: '', short: '', description: '', roleFor: 'admin', menus: [] };

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ total: 0, admin: 0, warehouse: 0, staff: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [roleFor, setRoleFor] = useState('');
  const [usage, setUsage] = useState('');
  const [notice, setNotice] = useState('');
  const seq = useRef(0);

  // create / edit
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [menuTree, setMenuTree] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuSearch, setMenuSearch] = useState('');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // view
  const [viewing, setViewing] = useState(null);
  const [viewTree, setViewTree] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  // delete
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const params = { page, limit: LIMIT, search: debounced, roleFor, usage };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('role/list', params, authToken(), false);
      if (mine !== seq.current) return;
      setRoles(res?.roles || []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
    } catch {
      if (mine === seq.current) setRoles([]);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, roleFor, usage]);
  useEffect(() => { load(); }, [load]);

  const fetchMenus = async (menuFor) => {
    const res = await getData('menu/list', { menuFor }, authToken(), false);
    return Array.isArray(res) ? res : [];
  };

  // ---------- form ----------
  const loadFormMenus = (menuFor) => {
    setMenuLoading(true);
    fetchMenus(menuFor).then(setMenuTree).catch(() => setMenuTree([])).finally(() => setMenuLoading(false));
  };
  const openForm = (role = null) => {
    setErrors({});
    setFormError('');
    setMenuSearch('');
    if (role) {
      setForm({ id: role._id, name: niceName(role.name), short: role.short || '', description: role.description || '', roleFor: role.roleFor || 'admin', menus: (role.menus || []).map(idOf) });
      loadFormMenus(role.roleFor || 'admin');
    } else {
      setForm(emptyForm);
      loadFormMenus('admin');
    }
    setViewing(null);
    setFormOpen(true);
  };
  const changeCategory = (_, val) => {
    if (!val || val === form.roleFor) return;
    setForm((f) => ({ ...f, roleFor: val, menus: [] }));
    loadFormMenus(val);
  };
  const toggleMain = (menu) => setForm((f) => {
    const ids = [String(menu._id), ...(menu.submenus || []).map((s) => String(s._id))];
    const on = f.menus.includes(String(menu._id));
    return { ...f, menus: on ? f.menus.filter((id) => !ids.includes(id)) : [...new Set([...f.menus, ...ids])] };
  });
  const toggleSub = (sub, menu) => setForm((f) => {
    const id = String(sub._id);
    if (f.menus.includes(id)) return { ...f, menus: f.menus.filter((x) => x !== id) };
    return { ...f, menus: [...new Set([...f.menus, id, String(menu._id)])] };
  });
  const allIds = menuTree.flatMap((m) => [String(m._id), ...(m.submenus || []).map((s) => String(s._id))]);
  const allSelected = allIds.length > 0 && allIds.every((id) => form.menus.includes(id));
  const q = menuSearch.trim().toLowerCase();
  const visibleTree = q
    ? menuTree.map((m) => ({ ...m, submenus: (m.submenus || []).filter((s) => String(s.name).toLowerCase().includes(q)), _own: String(m.name).toLowerCase().includes(q) }))
      .filter((m) => m._own || m.submenus.length)
      .map((m) => (m._own ? { ...m, submenus: (menuTree.find((x) => x._id === m._id)?.submenus) || [] } : m))
    : menuTree;

  const save = async (ev) => {
    ev.preventDefault();
    setFormError('');
    const e = {};
    if (!form.name.trim()) e.name = 'Role name is required';
    else if (form.name.trim().length < 2) e.name = 'Use at least 2 characters';
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), short: form.short.trim(), description: form.description.trim(), roleFor: form.roleFor, menus: JSON.stringify(form.menus) };
      if (form.id) await putData(`role/update/${form.id}`, payload, authToken());
      else await postData('role/add', payload, authToken());
      setFormOpen(false);
      setNotice(form.id ? 'Role updated. Everyone on it now has the new menus.' : 'Role created.');
      load();
    } catch (err) {
      setFormError(err?.message || 'Could not save the role');
    } finally {
      setSaving(false);
    }
  };

  // ---------- view ----------
  const openView = (role) => {
    setViewing(role);
    setViewTree([]);
    setViewLoading(true);
    Promise.all([
      getData(`role/${role._id}`, null, authToken(), false).catch(() => null),
      fetchMenus(role.roleFor || 'admin').catch(() => []),
    ]).then(([full, tree]) => {
      if (full) setViewing((cur) => (cur && cur._id === role._id ? { ...cur, ...full } : cur));
      setViewTree(tree);
    }).finally(() => setViewLoading(false));
  };

  // ---------- delete ----------
  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteData(`role/delete/${toDelete._id}`, authToken());
      setToDelete(null);
      setNotice('Role deleted.');
      if (roles.length === 1 && page > 1) setPage(page - 1); else load();
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete the role');
    } finally {
      setDeleting(false);
    }
  };

  const dirty = !!search || !!roleFor || !!usage;
  const assigned = new Set((viewing?.menus || []).map(idOf));

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Role management</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Decide which menus each kind of staff member can open.</Typography>
        </Box>
        <Button startIcon={<MdAdd />} variant="contained" onClick={() => openForm()} sx={yellowBtn}>Create role</Button>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="All roles" value={stats.total} icon={MdAdminPanelSettings} from="#6366f1" to="#4338ca" active={!roleFor} onClick={() => { setRoleFor(''); setPage(1); }} />
        <Kpi title="Admin CRM roles" value={stats.admin} icon={MdAdminPanelSettings} from="#0ea5e9" to="#0369a1" active={roleFor === 'admin'} onClick={() => { setRoleFor(roleFor === 'admin' ? '' : 'admin'); setPage(1); }} />
        <Kpi title="Warehouse roles" value={stats.warehouse} icon={MdWarehouse} from="#f59e0b" to="#b45309" active={roleFor === 'warehouse'} onClick={() => { setRoleFor(roleFor === 'warehouse' ? '' : 'warehouse'); setPage(1); }} />
        <Kpi title="Staff with a role" value={stats.staff} icon={MdGroups} from="#22c55e" to="#15803d" />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search role name, code or description…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Category" value={roleFor} onChange={(e) => { setRoleFor(e.target.value); setPage(1); }} sx={{ minWidth: 160 }}>
          <MenuItem value="">All categories</MenuItem>
          <MenuItem value="admin">Admin CRM</MenuItem>
          <MenuItem value="warehouse">Warehouse</MenuItem>
        </TextField>
        <TextField select size="small" label="Usage" value={usage} onChange={(e) => { setUsage(e.target.value); setPage(1); }} sx={{ minWidth: 170 }}>
          <MenuItem value="">All roles</MenuItem>
          <MenuItem value="used">In use by staff</MenuItem>
          <MenuItem value="unused">Not used by anyone</MenuItem>
        </TextField>
        {dirty && <Button onClick={() => { setSearch(''); setRoleFor(''); setUsage(''); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Role</TableCell>
                <TableCell sx={head}>Category</TableCell>
                <TableCell sx={head}>Description</TableCell>
                <TableCell sx={head} align="center">Menus</TableCell>
                <TableCell sx={head} align="center">Staff</TableCell>
                <TableCell sx={head}>Created</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(5)].map((_, i) => <TableRow key={i}>{[...Array(7)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && roles.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No roles match these filters.</TableCell></TableRow>}
              {!loading && roles.map((r) => {
                const c = forColor(r.roleFor);
                return (
                  <TableRow key={r._id} hover>
                    <TableCell sx={cell}>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, textTransform: 'capitalize' }}>{niceName(r.name)}</Typography>
                      {r.short && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>Code {r.short}</Typography>}
                    </TableCell>
                    <TableCell><Chip size="small" label={forLabel(r.roleFor)} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: c.bg, color: c.fg }} /></TableCell>
                    <TableCell sx={{ ...cell, maxWidth: 280, color: '#475569' }}><Typography noWrap sx={{ fontFamily: FONT, fontSize: 13.5 }} title={r.description}>{r.description || '—'}</Typography></TableCell>
                    <TableCell sx={cell} align="center">{(r.menus || []).length}</TableCell>
                    <TableCell sx={cell} align="center"><Chip size="small" label={r.userCount || 0} sx={{ fontWeight: 800, height: 22, background: r.userCount ? '#dcfce7' : '#f1f5f9', color: r.userCount ? '#166534' : '#64748b' }} /></TableCell>
                    <TableCell sx={cell}>{fmtDate(r.createdAt)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="View"><IconButton size="small" onClick={() => openView(r)} sx={iconBtn('#4f46e5')}><MdVisibility size={17} /></IconButton></Tooltip>{' '}
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openForm(r)} sx={iconBtn('#ca8a04')}><MdEdit size={17} /></IconButton></Tooltip>{' '}
                      <Tooltip title={r.userCount ? 'In use — reassign staff first' : 'Delete'}><span><IconButton size="small" onClick={() => { setDeleteError(''); setToDelete(r); }} sx={iconBtn('#dc2626')}><MdDeleteOutline size={17} /></IconButton></span></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} role{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* create / edit */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, borderBottom: '3px solid #6366f1', background: '#fff' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{form.id ? 'Edit role' : 'Create role'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{form.menus.length} menu item{form.menus.length === 1 ? '' : 's'} selected</Typography>
          </Box>
          <IconButton onClick={() => setFormOpen(false)} disabled={saving} size="small"><MdClose size={20} /></IconButton>
        </Box>
        <Box component="form" onSubmit={save} noValidate sx={{ display: 'contents' }}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            <Card title="Role details" accent="#6366f1">
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, mt: 1 }}>
                <TextField size="small" label="Role name *" placeholder="e.g. Sales Manager" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} error={!!errors.name} helperText={errors.name} />
                <TextField size="small" label="Short code" placeholder="e.g. SM" value={form.short} onChange={(e) => setForm({ ...form, short: e.target.value })} />
                <TextField size="small" label="Description" multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={{ gridColumn: { md: '1 / -1' } }} />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: '#64748b', mb: 0.75 }}>Category</Typography>
                <ToggleButtonGroup exclusive size="small" value={form.roleFor} onChange={changeCategory}>
                  <ToggleButton value="admin" sx={{ textTransform: 'none', fontWeight: 700, px: 2.5 }}>Admin CRM</ToggleButton>
                  <ToggleButton value="warehouse" sx={{ textTransform: 'none', fontWeight: 700, px: 2.5 }}>Warehouse</ToggleButton>
                </ToggleButtonGroup>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', mt: 0.75 }}>Changing the category clears the selected menus, since each app has its own menus.</Typography>
              </Box>
            </Card>

            <Card title="Menu access" accent="#f59e0b" right={
              <FormControlLabel sx={{ mr: 0 }} label={<Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>Select all</Typography>}
                control={<Checkbox size="small" checked={allSelected} indeterminate={!allSelected && form.menus.length > 0} onChange={() => setForm((f) => ({ ...f, menus: allSelected ? [] : allIds }))} />} />
            }>
              <TextField fullWidth size="small" placeholder="Filter menus…" value={menuSearch} onChange={(e) => setMenuSearch(e.target.value)} sx={{ my: 1 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
              <Box sx={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px', p: 1 }}>
                {menuLoading && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={22} /></Box>}
                {!menuLoading && visibleTree.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', textAlign: 'center', py: 3 }}>No menus found.</Typography>}
                {!menuLoading && visibleTree.map((menu) => {
                  const subs = menu.submenus || [];
                  const picked = subs.filter((s) => form.menus.includes(String(s._id))).length;
                  const on = form.menus.includes(String(menu._id));
                  return (
                    <Box key={menu._id} sx={{ mb: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={on} indeterminate={on && subs.length > 0 && picked > 0 && picked < subs.length} onChange={() => toggleMain(menu)} />}
                        label={<Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>{menu.name}{subs.length ? <Typography component="span" sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', fontWeight: 600 }}> · {picked}/{subs.length}</Typography> : null}</Typography>} />
                      <Box sx={{ pl: 4, display: 'flex', flexWrap: 'wrap', columnGap: 2 }}>
                        {subs.map((s) => (
                          <FormControlLabel key={s._id} sx={{ width: { xs: '100%', sm: 'calc(50% - 16px)' }, m: 0 }}
                            control={<Checkbox size="small" checked={form.menus.includes(String(s._id))} onChange={() => toggleSub(s, menu)} />}
                            label={<Typography sx={{ fontFamily: FONT, fontSize: 13 }}>{s.name}</Typography>} />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Card>
            {formError && <Alert severity="error">{formError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={() => setFormOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={yellowBtn}>{saving ? 'Saving…' : form.id ? 'Update role' : 'Create role'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* view */}
      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {viewing && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: '#fff' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20, textTransform: 'capitalize' }}>{niceName(viewing.name)}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.9 }}>{forLabel(viewing.roleFor)}{viewing.short ? ` · ${viewing.short}` : ''}</Typography>
              </Box>
              <IconButton onClick={() => setViewing(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1.5fr' }, alignItems: 'start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card title="About" accent="#0ea5e9">
                  {[['Category', forLabel(viewing.roleFor)], ['Short code', viewing.short], ['Menus granted', (viewing.menus || []).length], ['Created', fmtDate(viewing.createdAt)], ['Last updated', fmtDate(viewing.updatedAt)]].map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px dashed #e2e8f0' }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{k}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>{v || '—'}</Typography>
                    </Box>
                  ))}
                  {viewing.description && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#475569', mt: 1.25 }}>{viewing.description}</Typography>}
                </Card>
                <Card title={`Staff on this role (${viewing.userCount ?? viewing.users?.length ?? 0})`} accent="#22c55e">
                  {viewLoading && !viewing.users && <Skeleton height={60} />}
                  {viewing.users && viewing.users.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>Nobody is using this role yet.</Typography>}
                  {(viewing.users || []).map((u) => (
                    <Box key={u._id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.9, borderBottom: '1px dashed #e2e8f0' }}>
                      <Box>
                        <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{u.email}</Typography>
                      </Box>
                      <Chip size="small" label={u.status || '—'} sx={{ height: 20, fontWeight: 700, textTransform: 'capitalize', background: u.status === 'active' ? '#dcfce7' : '#f1f5f9', color: u.status === 'active' ? '#166534' : '#475569' }} />
                    </Box>
                  ))}
                </Card>
              </Box>
              <Card title="Menu access" accent="#f59e0b" right={<Chip size="small" label={`${assigned.size} granted`} sx={{ fontWeight: 700 }} />}>
                {viewLoading && viewTree.length === 0 && <Skeleton height={140} />}
                {!viewLoading && viewTree.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>Menus could not be loaded.</Typography>}
                <Box sx={{ maxHeight: 440, overflowY: 'auto' }}>
                  {viewTree.map((m) => {
                    const mainOn = assigned.has(String(m._id));
                    const subs = m.submenus || [];
                    if (!mainOn && !subs.some((s) => assigned.has(String(s._id)))) return null;
                    return (
                      <Box key={m._id} sx={{ py: 1, borderBottom: '1px dashed #e2e8f0' }}>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>{m.name}</Typography>
                        {subs.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                            {subs.map((s) => (
                              <Chip key={s._id} size="small" label={s.name} sx={{ fontFamily: FONT, fontWeight: 600, height: 22, background: assigned.has(String(s._id)) ? '#dcfce7' : '#f1f5f9', color: assigned.has(String(s._id)) ? '#166534' : '#94a3b8', textDecoration: assigned.has(String(s._id)) ? 'none' : 'line-through' }} />
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                  {!viewLoading && viewTree.length > 0 && assigned.size === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>No menus are granted to this role.</Typography>}
                </Box>
              </Card>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setViewing(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
              <Button onClick={() => openForm(viewing)} startIcon={<MdEdit />} variant="contained" sx={yellowBtn}>Edit role</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* delete */}
      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>Delete role?</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>
            <b style={{ textTransform: 'capitalize' }}>{niceName(toDelete?.name)}</b> will be removed permanently.
            {toDelete?.userCount ? ` ${toDelete.userCount} staff member(s) still use it, so the server will refuse until they are moved.` : ''}
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setToDelete(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
